import React, { useState, useEffect, useRef } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import VinhosOverlay from './VinhosOverlay';
import CartPreview from './CartPreview';
import { useCart } from '../context/CartContext';
import { useT, useLanguage } from '../i18n/LanguageContext';
import logoDouroNostrum from '../assets/logo_douronostrum_mono.svg';

const NAV_LINKS = [
  { to: '/',         key: 'nav.home'     },
  { to: '/vinhos',   key: 'nav.vinhos', hasOverlay: true },
  { to: '/loja',     key: 'nav.loja'     },
  { to: '/vinhas',   key: 'nav.vinhas'   },
  { to: '/sobre',    key: 'nav.sobre'    },
  { to: '/contacto', key: 'nav.contacto' },
];

export default function Navbar() {
  const [scrolled,    setScrolled]    = useState(false);
  const [menuOpen,    setMenuOpen]    = useState(false);
  const [overlayOpen, setOverlayOpen] = useState(false);

  const { totalItems } = useCart();
  const { pathname }   = useLocation();
  const isHome         = pathname === '/';
  const t              = useT();
  const { lang, setLang } = useLanguage();

  // Timer para dar margem entre sair do link e entrar no overlay
  const closeTimer     = useRef(null);
  // Timer para o preview do carrinho
  const cartCloseTimer = useRef(null);
  const [cartOpen, setCartOpen] = useState(false);

  const clearCloseTimer = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  const openOverlay = () => {
    clearCloseTimer();
    setOverlayOpen(true);
  };

  const scheduleClose = () => {
    clearCloseTimer();
    closeTimer.current = setTimeout(() => setOverlayOpen(false), 120);
  };

  const closeOverlayNow = () => {
    clearCloseTimer();
    setOverlayOpen(false);
  };

  // ── Cart preview handlers ──
  const clearCartCloseTimer = () => {
    if (cartCloseTimer.current) {
      clearTimeout(cartCloseTimer.current);
      cartCloseTimer.current = null;
    }
  };
  const openCartPreview = () => {
    clearCartCloseTimer();
    setCartOpen(true);
  };
  const scheduleCartClose = () => {
    clearCartCloseTimer();
    cartCloseTimer.current = setTimeout(() => setCartOpen(false), 150);
  };
  const closeCartNow = () => {
    clearCartCloseTimer();
    setCartOpen(false);
  };

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Bloqueia scroll da página apenas quando o menu mobile está aberto
  // (o overlay de vinhos não bloqueia scroll para não impedir navegação)
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  // ESC fecha overlay
  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') closeOverlayNow(); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);

  // O nav começa transparente em todas as páginas (os heroes têm fundo escuro)
  // e só fica "solid" quando o utilizador faz scroll ou o overlay está aberto.
  const isSolid = scrolled || overlayOpen;

  const navClass = [
    'nav',
    isSolid ? 'nav--solid' : 'nav--transparent',
    scrolled ? 'nav--compact' : '',
  ].filter(Boolean).join(' ');

  // Cor dos textos: branco apenas na Home em estado transparente (hero escuro)
  // Nas restantes páginas o fundo do topo é claro → texto preto sempre
  const textOnDark = isHome && !isSolid;
  const linkColor = textOnDark ? 'rgb(253, 253, 253)' : 'var(--clr-noir)';
  // brightness(0) → preto puro, depois invert(1) → branco quase puro (#fdfdfd)
  const logoFilter = textOnDark
    ? 'brightness(0) invert(1) brightness(0.992)'
    : 'none';

  return (
    <>
      <nav className={navClass} role="navigation" aria-label="Navegação principal">
        <div className="nav__inner">

          {/* ── Linha 1: Logo centrado ── */}
          <div className="nav__row nav__row--logo">
            <Link
              to="/"
              className="nav__logo"
              aria-label="Douro Nostrum — página inicial"
              onClick={() => setMenuOpen(false)}
            >
              <img
                src={logoDouroNostrum}
                alt="Douro Nostrum"
                className="nav__logo-img"
                style={{ filter: logoFilter }}
              />
            </Link>

            {/* Toggle PT/EN + Carrinho — absoluto à direita nesta linha em desktop */}
            <div className="nav__right">
              <div
                className="nav__lang"
                role="group"
                aria-label="Language"
                style={{ color: linkColor }}
              >
                <button
                  type="button"
                  onClick={() => setLang('pt')}
                  className={`nav__lang-btn${lang === 'pt' ? ' nav__lang-btn--active' : ''}`}
                  aria-pressed={lang === 'pt'}
                >
                  PT
                </button>
                <span className="nav__lang-sep" aria-hidden="true">|</span>
                <button
                  type="button"
                  onClick={() => setLang('en')}
                  className={`nav__lang-btn${lang === 'en' ? ' nav__lang-btn--active' : ''}`}
                  aria-pressed={lang === 'en'}
                >
                  EN
                </button>
              </div>

              <div
                className="nav__cart-wrapper"
                onMouseEnter={openCartPreview}
                onMouseLeave={scheduleCartClose}
              >
                <Link
                  to="/carrinho"
                  className="nav__cart nav__cart--top"
                  style={{ color: linkColor, position: 'static' }}
                  aria-label={t('nav.cart.aria', {
                    n: totalItems,
                    n_word: totalItems === 1 ? t('nav.cart.itemSingular') : t('nav.cart.itemPlural'),
                  })}
                  onClick={closeCartNow}
                >
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.3" aria-hidden="true">
                    <path d="M5 7 H19 L17.5 17 H6.5 L5 7 Z" />
                    <path d="M8 7 C8 4.8 9.8 3 12 3 C14.2 3 16 4.8 16 7" />
                  </svg>
                  {totalItems > 0 && (
                    <span className="nav__cart-badge">{totalItems}</span>
                  )}
                </Link>
                <CartPreview
                  open={cartOpen}
                  onMouseEnter={openCartPreview}
                  onMouseLeave={scheduleCartClose}
                  onClose={closeCartNow}
                />
              </div>
            </div>

            {/* Hamburger mobile (também absoluto à direita) */}
            <button
              className="nav__hamburger"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label={menuOpen ? t('nav.menuClose') : t('nav.menuOpen')}
              aria-expanded={menuOpen}
              style={{ color: linkColor }}
            >
              <span style={{ transform: menuOpen ? 'rotate(45deg) translate(4px, 4px)' : 'none' }} />
              <span style={{ opacity: menuOpen ? 0 : 1 }} />
              <span style={{ transform: menuOpen ? 'rotate(-45deg) translate(4px, -4px)' : 'none' }} />
            </button>
          </div>

          {/* ── Linha 2: Links centrados ── */}
          <ul className="nav__links" role="list">
            {NAV_LINKS.map(({ to, key, hasOverlay }) => (
              <li
                key={to}
                onMouseEnter={hasOverlay ? openOverlay : undefined}
                onMouseLeave={hasOverlay ? scheduleClose : undefined}
              >
                <NavLink
                  to={to}
                  end={to === '/'}
                  className={({ isActive }) =>
                    `nav__link${isActive ? ' nav__link--active' : ''}${hasOverlay ? ' nav__link--has-overlay' : ''}`
                  }
                  style={{ color: linkColor }}
                  onClick={hasOverlay ? closeOverlayNow : undefined}
                >
                  {t(key)}
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
      </nav>

      {/* ── Overlay Vinhos ── */}
      <VinhosOverlay
        open={overlayOpen}
        onMouseEnter={openOverlay}
        onMouseLeave={scheduleClose}
        onClose={closeOverlayNow}
      />

      {/* ── Menu Mobile ── */}
      <div
        className={`nav__mobile-menu${menuOpen ? ' nav__mobile-menu--open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Menu"
      >
        {NAV_LINKS.map(({ to, key }, i) => (
          <NavLink
            key={to}
            to={to}
            end={to === '/'}
            className="nav__mobile-link"
            onClick={() => setMenuOpen(false)}
            style={{ animationDelay: `${i * 60}ms` }}
          >
            {t(key)}
          </NavLink>
        ))}
        <Link
          to="/carrinho"
          className="btn btn-lg btn--on-dark"
          onClick={() => setMenuOpen(false)}
          style={{ marginTop: 'var(--sp-6)' }}
        >
          {t('nav.cartLabel')} {totalItems > 0 && `(${totalItems})`}
        </Link>

        {/* Toggle PT/EN dentro do menu mobile */}
        <div className="nav__mobile-lang" role="group" aria-label="Language">
          <button
            type="button"
            onClick={() => setLang('pt')}
            className={lang === 'pt' ? 'is-active' : ''}
            aria-pressed={lang === 'pt'}
          >
            PT
          </button>
          <span aria-hidden="true">|</span>
          <button
            type="button"
            onClick={() => setLang('en')}
            className={lang === 'en' ? 'is-active' : ''}
            aria-pressed={lang === 'en'}
          >
            EN
          </button>
        </div>
      </div>
    </>
  );
}
